import { useState, type JSX } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { ChevronDown, BarChart2 } from 'lucide-react';
import clsx from 'clsx';

// ── Date range options ──────────────────────────────────────────────────────

type RangeId = '7d' | '30d' | '90d' | 'month' | 'last_month';

const RANGES: { id: RangeId; label: string }[] = [
  { id: '7d', label: 'Last 7 Days' },
  { id: '30d', label: 'Last 30 Days' },
  { id: '90d', label: 'Last 90 Days' },
  { id: 'month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
];

// ── Custom dropdown ─────────────────────────────────────────────────────────

interface RangeDropdownProps {
  value: RangeId;
  onChange: (value: RangeId) => void;
}

function RangeDropdown({ value, onChange }: RangeDropdownProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const selected = RANGES.find((r) => r.id === value) || RANGES[1];
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-surface-3 border border-border rounded-md
                   text-ink-secondary hover:border-accent transition-colors cursor-pointer"
      >
        {selected.label}
        <ChevronDown size={10} className={clsx('transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute top-full right-0 z-50 mt-1 bg-surface-3 border border-border rounded-md shadow-lg overflow-hidden min-w-[140px]">
            {RANGES.map((r) => (
              <button
                key={r.id}
                onClick={() => {
                  onChange(r.id);
                  setOpen(false);
                }}
                className={clsx(
                  'w-full text-left px-3 py-1.5 text-xs hover:bg-surface-4 transition-colors cursor-pointer',
                  r.id === value ? 'text-accent' : 'text-ink-primary'
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Custom tooltip ──────────────────────────────────────────────────────────
// Recharts passes an `active/payload/label` trio to custom <Tooltip content>.
// We type the payload loosely here because we only read `color`, `name`, and
// `value`; the full Recharts type includes many chart-specific fields.

interface TooltipPayloadEntry {
  color?: string;
  name?: string;
  value?: number;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
  format?: 'pct' | 'dollar';
}

function ChartTooltip({ active, payload, label, format }: ChartTooltipProps): JSX.Element | null {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-3 border border-border rounded-md px-3 py-2 shadow-lg text-xs">
      <div className="text-ink-muted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-ink-secondary">{p.name}:</span>
          <span className="font-mono text-ink-bright">
            {format === 'pct'
              ? `${(p.value ?? 0).toFixed(1)}%`
              : `$${(p.value ?? 0).toLocaleString()}`}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Empty chart state ───────────────────────────────────────────────────────

function EmptyChart({ label }: { label: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center">
      <BarChart2 size={48} className="text-ink-muted mb-2" />
      <p className="text-sm text-ink-muted">{label}</p>
    </div>
  );
}

// ── ChartsRow ───────────────────────────────────────────────────────────────

export interface RevenueDatum {
  date: string;
  revenue?: number;
  profit?: number;
  prev_revenue?: number;
  prev_profit?: number;
}

export interface RoiDatum {
  date: string;
  roi?: number;
  prev_roi?: number;
}

interface ChartsRowProps {
  revenueData?: RevenueDatum[];
  roiData?: RoiDatum[];
}

export default function ChartsRow({ revenueData = [], roiData = [] }: ChartsRowProps): JSX.Element {
  const [range, setRange] = useState<RangeId>('30d');
  const [periodCompare, setPeriodCompare] = useState<boolean>(false);

  const gridStroke = 'var(--border-subtle)';
  const axisStyle = {
    fontSize: 11,
    fill: 'var(--ink-muted)',
    fontFamily: "'JetBrains Mono', monospace",
  };

  const hasRevenue = revenueData.length > 0;
  const hasRoi = roiData.length > 0;

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={() => setPeriodCompare((v) => !v)}
          role="switch"
          aria-checked={periodCompare}
          aria-label="Toggle period comparison"
          className={clsx(
            'flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer',
            periodCompare
              ? 'bg-accent/10 border-accent/30 text-accent'
              : 'bg-surface-3 border-border text-ink-muted hover:text-ink-secondary'
          )}
        >
          <span
            className={clsx(
              'w-5 h-3 rounded-full flex items-center transition-colors',
              periodCompare ? 'bg-accent justify-end' : 'bg-surface-5 justify-start'
            )}
          >
            <span
              className={clsx(
                'w-2 h-2 rounded-full mx-0.5',
                periodCompare ? 'bg-white' : 'bg-ink-muted'
              )}
            />
          </span>
          Period comparison
        </button>
        <RangeDropdown value={range} onChange={setRange} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-3">
        {/* Revenue & Profit */}
        <div className="arbi-card p-4">
          <h3 className="text-xs font-medium text-ink-secondary uppercase tracking-wider mb-3">
            Revenue & Profit
          </h3>
          <div style={{ height: 220 }}>
            {hasRevenue ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={axisStyle}
                    axisLine={{ stroke: gridStroke }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={axisStyle}
                    axisLine={{ stroke: gridStroke }}
                    tickLine={false}
                    tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`}
                  />
                  <Tooltip content={<ChartTooltip format="dollar" />} />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="var(--ink-secondary)"
                    strokeWidth={2}
                    dot={false}
                    name="Revenue"
                  />
                  <Line
                    type="monotone"
                    dataKey="profit"
                    stroke="var(--accent)"
                    strokeWidth={2}
                    dot={false}
                    name="Profit"
                  />
                  {periodCompare && (
                    <>
                      <Line
                        type="monotone"
                        dataKey="prev_revenue"
                        stroke="var(--ink-secondary)"
                        strokeWidth={1}
                        strokeDasharray="4 4"
                        dot={false}
                        name="Prev Revenue"
                      />
                      <Line
                        type="monotone"
                        dataKey="prev_profit"
                        stroke="var(--accent)"
                        strokeWidth={1}
                        strokeDasharray="4 4"
                        dot={false}
                        name="Prev Profit"
                      />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="No sales data yet" />
            )}
          </div>
        </div>

        {/* ROI % */}
        <div className="arbi-card p-4">
          <h3 className="text-xs font-medium text-ink-secondary uppercase tracking-wider mb-3">
            ROI% Trend
          </h3>
          <div style={{ height: 220 }}>
            {hasRoi ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={roiData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={axisStyle}
                    axisLine={{ stroke: gridStroke }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={axisStyle}
                    axisLine={{ stroke: gridStroke }}
                    tickLine={false}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <Tooltip content={<ChartTooltip format="pct" />} />
                  <Line
                    type="monotone"
                    dataKey="roi"
                    stroke="var(--accent)"
                    strokeWidth={2}
                    dot={false}
                    name="ROI%"
                  />
                  {periodCompare && (
                    <Line
                      type="monotone"
                      dataKey="prev_roi"
                      stroke="var(--accent)"
                      strokeWidth={1}
                      strokeDasharray="4 4"
                      dot={false}
                      name="Prev ROI%"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="No ROI data yet" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
